import { useEffect, useMemo, useState } from 'react';
import { machineEventCycleStatistics } from '@reducers/statistics';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { LIMIT } from '@v2/pages/common/components/NCFileBatchEvents/constants';
import { NCFileVersion } from '@interfaces/search';
import getPcsCount from '@components/ncFileVersions/helpers/getPcsCount';
import {
  addCyclePcsCounts,
  arePcsCountsEqual,
  createInitialVersionPcsCounts,
  mergePcsCountsIntoVersions,
  NCFileVersionPcsCounts,
} from '@components/ncFileVersions/helpers/ncFileVersionPcsCounts';

interface UseNCFileVersionsWithPcsCountsParams {
  ncFileId?: number | null;
  versions: NCFileVersion[] | null | undefined;
}

export const useNCFileVersionsWithPcsCounts = ({ ncFileId, versions }: UseNCFileVersionsWithPcsCountsParams): NCFileVersion[] => {
  const dispatch = useAppDispatch();
  const versionList = useMemo(() => versions ?? [], [versions]);
  const [versionPcsCounts, setVersionPcsCounts] = useState<NCFileVersionPcsCounts>({});

  useEffect(() => {
    if (!ncFileId || !versionList.length) {
      setVersionPcsCounts((prev) => (arePcsCountsEqual(prev, {}) ? prev : {}));
      return;
    }

    const shouldLoadPcs = versionList.some((version) => typeof getPcsCount(version) !== 'number');
    if (!shouldLoadPcs) {
      setVersionPcsCounts((prev) => (arePcsCountsEqual(prev, {}) ? prev : {}));
      return;
    }

    const controller = new AbortController();
    let isActive = true;

    const fetchCounts = async () => {
      try {
        let completeCounts = createInitialVersionPcsCounts(versionList);
        let pageNumber = 1;
        let hasMore = true;

        while (hasMore) {
          const response = await dispatch(
            machineEventCycleStatistics(
              {
                ncFileId,
                pagination: {
                  includeCount: false,
                  pageNumber,
                  pageSize: LIMIT,
                },
              },
              controller.signal
            )
          );

          if (!isActive) {
            return;
          }

          const cycles = response?.cycles ?? [];
          completeCounts = addCyclePcsCounts(completeCounts, cycles);
          hasMore = cycles.length >= LIMIT;
          pageNumber++;
        }

        setVersionPcsCounts((prev) => (arePcsCountsEqual(prev, completeCounts) ? prev : completeCounts));
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Failed to load pcs counts for NC file versions', error);
          setVersionPcsCounts((prev) => (arePcsCountsEqual(prev, {}) ? prev : {}));
        }
      }
    };

    fetchCounts();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [dispatch, ncFileId, versionList]);

  return useMemo(() => mergePcsCountsIntoVersions(versionList, versionPcsCounts), [versionList, versionPcsCounts]);
};
